/* eslint-disable react/require-default-props */
import React, { useRef, useState } from 'react';
import { Button, FlexColumn, FlexRow, Typography } from 'design-system';

const MAX_FILE_SIZE_MB = 10;

function normalizeFiles(value) {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

export default function UploadFile({
  preview = false,
  value,
  onChange,
  interactive = false,
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const files = normalizeFiles(value);

  const applyFiles = (fileList) => {
    if (!interactive || !fileList?.length) return;

    const accepted = Array.from(fileList).filter(
      (file) => file.size <= MAX_FILE_SIZE_MB * 1024 * 1024
    );

    if (!accepted.length) return;

    const merged = [...files];
    accepted.forEach((file) => {
      const isDuplicate = merged.some((item) => item.name === file.name && item.size === file.size);
      if (!isDuplicate) {
        merged.push(file);
      }
    });

    onChange?.(merged);
  };

  const openPicker = () => {
    if (!interactive) return;
    inputRef.current?.click();
  };

  const handleInputChange = (event) => {
    applyFiles(event.target.files);
    const input = event.target;
    input.value = '';
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    applyFiles(event.dataTransfer?.files);
  };

  const dropzoneClassName = [
    'cc-preview-file-upload',
    preview ? '' : 'py-3 px-5',
    dragOver ? 'cc-preview-file-upload--drag-over' : '',
    files.length ? 'cc-preview-file-upload--has-files' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <FlexColumn align="stretch" fullWidth className="cc-preview-file-upload-wrap">
      <FlexColumn
        align="center"
        justify="center"
        fullWidth
        className={dropzoneClassName}
        onDragOver={(event) => {
          if (!interactive) return;
          event.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          className="cc-preview-file-upload__input"
          multiple
          disabled={!interactive}
          onChange={handleInputChange}
        />

        <Typography component="div" variant="body1" aria-hidden>
          <i className="fas fa-cloud-upload-alt cc-preview-file-upload__icon" />
        </Typography>

        <Typography variant="body2" component="p" className="cc-preview-file-upload__hint">
          Drag your files here, file size less than {MAX_FILE_SIZE_MB} MB or
        </Typography>

        <Button
          type="button"
          variant="link"
          className="cc-preview-file-upload__browse"
          disabled={!interactive}
          onClick={openPicker}
        >
          Browse Files
        </Button>
      </FlexColumn>

      {files.length ? (
        <FlexColumn align="stretch" fullWidth className="cc-preview-file-upload__list">
          {files.map((file) => (
            <FlexRow
              key={`${file.name}-${file.size}`}
              align="center"
              justify="between"
              fullWidth
              className="cc-preview-file-upload__item"
            >
              <Typography component="span" variant="body2" className="cc-preview-file-upload__name">
                {file.name}
              </Typography>
              {interactive ? (
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="cc-preview-file-upload__remove"
                  onClick={() => {
                    const next = files.filter((item) => item.name !== file.name || item.size !== file.size);
                    onChange?.(next.length ? next : null);
                  }}
                >
                  Remove
                </Button>
              ) : null}
            </FlexRow>
          ))}
        </FlexColumn>
      ) : null}
    </FlexColumn>
  );
}
